/**
 * Wishlist demand — Stage 4.
 *
 * Sources `/api/bi/wishlist/demand/`, aggregated from `WishlistItem` joined
 * to `ProductAvailability` so we attribute demand by store + brand and can
 * spotlight stockouts that real customers are waiting on.
 */
import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Heart, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { useWishlistDemand, type WishlistDemandRow } from '@/api/bi';
import { useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function WishlistPage() {
  const storeIds = useFilters((s) => s.storeIds);
  const [stockoutOnly, setStockoutOnly] = useState(false);
  const [search, setSearch] = useState('');
  const query = useWishlistDemand({ storeIds, limit: 200 });

  const all = query.data?.rows ?? [];
  const filtered = useMemo(() => {
    let rows = all;
    if (stockoutOnly) rows = rows.filter((r) => r.stockout);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.product_name?.toLowerCase().includes(q) ||
          (r.brand ?? '').toLowerCase().includes(q) ||
          (r.store_name ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [all, stockoutOnly, search]);

  const columns = useMemo<ColumnDef<WishlistDemandRow, unknown>[]>(
    () => [
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.original.product_name}</div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.brand ?? '—'} · {row.original.store_name}
            </div>
          </div>
        ),
      },
      {
        id: 'stock',
        header: 'Stock',
        cell: ({ row }) => {
          if (row.original.stockout) {
            return (
              <Badge tone="danger" className="gap-1">
                <AlertCircle className="h-3 w-3" /> stockout
              </Badge>
            );
          }
          if (row.original.stock < row.original.wishes) {
            return <Badge tone="warning">low · {row.original.stock}</Badge>;
          }
          return <Badge tone="success">in stock · {row.original.stock}</Badge>;
        },
      },
      {
        id: 'wishes',
        header: () => <div className="text-right">Wishes</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.number(row.original.wishes)}
          </div>
        ),
      },
      {
        id: 'people',
        header: () => <div className="text-right">People</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.unique_users)}</div>
        ),
      },
      {
        id: 'gap',
        header: () => <div className="text-right">Demand gap</div>,
        cell: ({ row }) => {
          const gap = row.original.demand_supply_gap;
          if (gap <= 0) {
            return <div className="text-right text-xs text-muted-foreground">—</div>;
          }
          return (
            <div className="kpi-num text-right text-sm font-medium text-warning">
              +{fmt.number(gap)}
            </div>
          );
        },
      },
      {
        id: 'price',
        header: () => <div className="text-right">Price</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.npr(row.original.price)}</div>
        ),
      },
      {
        id: 'last',
        header: 'Last added',
        cell: ({ row }) =>
          row.original.last_added ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_added), 'MMM d, HH:mm')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Merchandising"
        title="Wishlist demand"
        description="Where saved-for-later turns into a missed sale. Sorted by raw wishes — sift the top by stockouts."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Total wishes"
          value={fmt.number(query.data?.totals.wishes_total ?? 0)}
          loading={query.isLoading}
          hint="Active wishlist rows in your stores."
        />
        <KPICard
          label="Wishful shoppers"
          value={fmt.number(query.data?.totals.unique_users ?? 0)}
          loading={query.isLoading}
          hint="Distinct users with at least one wish."
        />
        <KPICard
          label="Top wishes out of stock"
          value={fmt.number(query.data?.totals.top_stockout_count ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          hint="Among the top demand rows shown below."
        />
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Heads up</CardTitle>
            <Badge tone="neutral">live from /api/bi/wishlist/demand/</Badge>
          </CardHeader>
          <CardBody className="text-xs text-muted-foreground">
            Anything in the table marked <strong className="text-warning">low</strong> or{' '}
            <strong className="text-danger">stockout</strong> is a queue of customers waiting on a
            replenishment. Pair with the search insights to prioritise.
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load wishlist demand." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<WishlistDemandRow>
            title="Most wished products"
            data={filtered}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search product, brand or store…' }}
            emptyTitle={stockoutOnly ? 'No stockouts in the wishlist top 200' : 'No wishlist demand yet'}
            toolbar={
              <button
                type="button"
                onClick={() => setStockoutOnly((v) => !v)}
                className={cn(
                  'press inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] focus-ring',
                  stockoutOnly
                    ? 'border-danger/40 bg-danger/10 text-danger'
                    : 'border-border bg-surface text-muted-foreground hover:text-foreground',
                )}
              >
                <Heart className="h-3 w-3" /> stockouts only
              </button>
            }
          />
        )}
      </div>
    </>
  );
}
